// ActionOffer — the transient turn-epilogue hand-off, as an inline card.
//
// LANE S3 / A-17: at turn end the agent may offer one or more CONCRETE
// executable next moves ("Open the material", "Re-export as HTML", "Try
// again", "Upload the file again"). This is that offer's RENDER surface.
// Each move maps to an EXISTING command handler the surface already wires —
// `onOpenMaterial` / `onOpenExport` / `onDownloadExport` / `onRequestUpload`
// (F9/F15, opens the shell's file picker for one Source) — or re-drives the
// agent via `onSendPrompt` (the `send_prompt` bridge for moves with no
// client command yet). A move whose handler is absent is NOT rendered — no
// dead buttons, the same no-dead-button posture the entity cards follow.
//
// Pure/props-driven: an `ActionOfferEntry` in, a card out; nothing here
// computes, fetches, or mutates. The move dispatch is an EXHAUSTIVE match over
// `ChatActionMove` (a new move kind breaks compilation here until handled).
//
// alarm-discipline: an offer is a QUIET hand-off, not an alarm — it never
// steals focus; the `recovery` tone (an error hand-off) is carried by an icon +
// a heading word, never colour alone.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../Button/Button.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { ActionOfferEntry, ChatActionMove } from "../types.js";
import "./ActionOffer.css";

export type ActionOfferProps = {
    readonly entry: ActionOfferEntry;
    readonly onOpenMaterial?: (materialId: string) => void;
    readonly onOpenExport?: (exportId: string) => void;
    readonly onDownloadExport?: (exportId: string) => void;
    // Re-drive the agent with a typed instruction (the `send_prompt` bridge).
    readonly onSendPrompt?: (prompt: string) => void;
    // Open the shell's file picker for ONE Source whose stored content went
    // missing (F9/F15, the `request_upload` move) — wired to the EXISTING
    // `reuploadSourceContent` round-trip, never a new upload path.
    readonly onRequestUpload?: (
        sourceId: string,
        sourceTitle: string | undefined,
    ) => void;
    readonly className?: string;
};

// The click handler for one move, or `undefined` when its handler is not wired
// (the button is then omitted). Exhaustive over the move sum.
const handlerFor = (
    move: ChatActionMove,
    props: ActionOfferProps,
): (() => void) | undefined => {
    switch (move.type) {
        case "open_material":
            return props.onOpenMaterial === undefined
                ? undefined
                : () => props.onOpenMaterial?.(move.materialId);
        case "open_export":
            return props.onOpenExport === undefined
                ? undefined
                : () => props.onOpenExport?.(move.exportId);
        case "download_export":
            return props.onDownloadExport === undefined
                ? undefined
                : () => props.onDownloadExport?.(move.exportId);
        case "send_prompt":
            return props.onSendPrompt === undefined
                ? undefined
                : () => props.onSendPrompt?.(move.prompt);
        case "request_upload":
            return props.onRequestUpload === undefined
                ? undefined
                : () =>
                      props.onRequestUpload?.(move.sourceId, move.sourceTitle);
        default:
            return exhaustSwitchCase(move);
    }
};

// The button's displayed text. Every OTHER move renders the agent's own
// `label` verbatim (it authors the phrase). `request_upload` is the one
// exception: the move always performs the exact same mechanical action
// (open the file picker for this source), so the surface controls a
// canonical, localized copy instead of trusting the agent's freeform text —
// the wire `label` still rides along for schema parity but is unused here.
const labelFor = (move: ChatActionMove): ReactNode => {
    switch (move.type) {
        case "open_material":
        case "open_export":
        case "download_export":
        case "send_prompt":
            return move.label;
        case "request_upload":
            return move.sourceTitle === undefined ? (
                <Trans>Upload the file again</Trans>
            ) : (
                <Trans>Upload “{move.sourceTitle}” again</Trans>
            );
        default:
            return exhaustSwitchCase(move);
    }
};

export const ActionOffer = (props: ActionOfferProps) => {
    const { entry, className } = props;
    const recovery = entry.tone === "recovery";
    // Pair each move with its resolved handler; drop the ones with no wired
    // handler (no dead buttons). The first move reads as the primary action.
    const actionable = entry.moves
        .map((move) => ({ move, run: handlerFor(move, props) }))
        .filter(
            (m): m is { move: ChatActionMove; run: () => void } =>
                m.run !== undefined,
        );
    if (actionable.length === 0) return null;
    return (
        <section
            className={`bilda-action-offer${
                recovery ? " bilda-action-offer--recovery" : ""
            }${className ? ` ${className}` : ""}`}
        >
            {recovery ? (
                <div className="bilda-action-offer__head">
                    <Icon name="warning" />
                    <Text as="span" size="sm" tone="dim">
                        <Trans>Something went wrong — try one of these:</Trans>
                    </Text>
                </div>
            ) : null}
            <div className="bilda-action-offer__moves">
                {actionable.map(({ move, run }, i) => (
                    <Button
                        key={`${move.type}-${move.label}`}
                        type="button"
                        size="sm"
                        variant={i === 0 && !recovery ? "primary" : "secondary"}
                        onClick={run}
                    >
                        {labelFor(move)}
                    </Button>
                ))}
            </div>
        </section>
    );
};
